<script setup>
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import DataTable from '@/components/DataTable.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import BookmarkInput from '@/components/BookmarkInput.vue'
  import { ref, inject, onMounted, computed } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useElectronicApplicationStore } from '@/stores/electronicApplicationStore'

  const router = useRouter()
  const route = useRoute()

  const dialogStore = useDialogStore()
  const eApplicationStore = useElectronicApplicationStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: '/dashboard' }, { name: '電子申請' }]

  // リファレンス
  const showPopupEdit = ref(false)
  const isEdit = ref(false)

  const searchText = ref('')
  const searchKey = 'status'
  const searchSelect = ref('')
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')
  const searchColor = ref('')

  // computed  ///////////////////////////////////////////
  const searchList = computed(() => eApplicationStore.statusOptions)

  const companyOptions = computed(() => {
    return eApplicationStore.companyNameOptions || []
  })

  const branchOptions = computed(() => {
    return eApplicationStore.branchNameOptions || []
  })

  const staffOptions = computed(() => {
    return eApplicationStore.staffNameOptions || []
  })

  // 会社・事業場・従業員セレクト状態
  const companySelectState = computed(() => eApplicationStore.getCompanySelectState(isEdit.value))
  const branchSelectState = computed(() => eApplicationStore.getBranchSelectState(isEdit.value))
  const staffSelectState = computed(() => eApplicationStore.getStaffSelectState(isEdit.value))

  // methods ///////////////////////////////////////////
  onMounted(async () => {
    // ステータスで絞り込み
    if (route.query.status) {
      searchSelect.value = eApplicationStore.getStatusValueFromLabel(route.query.status)
    }

    // テーブル定義を初期化
    eApplicationStore.initTableList()

    // 会社名+健保情報リストを取得（電子申請専用API）
    await eApplicationStore.getCompanyNamesWithHealthInsurance()

    // 電子申請データを取得
    await eApplicationStore.getApplications()

    if (route.query.action === 'new') {
      openEditDialog()
    }
  })

  // 保存
  const onSave = async () => {
    // 保存処理
    const message = await eApplicationStore.saveApplication()

    if (message) {
      await dialogStore.showMessageDialog('保存', message)
      showPopupEdit.value = false
    }
  }

  // コピー
  const onCopy = () => {
    eApplicationStore.copyApplication()
    isEdit.value = false
  }

  // 削除
  const onDelete = async () => {
    if (await dialogStore.showConfirmDialog('確認', 'この電子申請データを削除してもよろしいですか？')) {
      const message = await eApplicationStore.deleteApplication()

      if (message) {
        await dialogStore.showMessageDialog('削除', message)
        showPopupEdit.value = false
      }
    }
  }

  // モーダル表示
  const openEditDialog = async item => {
    isEdit.value = item ? true : false

    await eApplicationStore.setFormData(item)
    showPopupEdit.value = true
  }

  // 会社変更時の処理（統一されたメソッドを使用）
  const onCompanyChange = async () => {
    await eApplicationStore.onCompanyChange()
  }

  // 事業場変更時の処理（統一されたメソッドを使用）
  const onBranchChange = async () => {
    await eApplicationStore.onBranchChange()
  }

  // 従業員変更時の処理
  const onStaffChange = () => {
    eApplicationStore.onStaffChange()
  }

  // 日付変更時の処理
  const onDateChange = (type, event) => {
    eApplicationStore.updateDateInfo(type, event.target.value)
  }

  // チェックボックス変更時の処理（新規追加）
  const onCheckChange = (groupKey, event) => {
    const isChecked = event.target.checked
    eApplicationStore.updateCheckStatus(groupKey, isChecked)
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>電子申請</h2>
      <button type="button" class="new" @click="openEditDialog()">新規登録</button>
      <div class="filters">
        <div class="area">
          <BookmarkInput v-model="searchColor" />
        </div>
        <div class="select">
          状況:
          <select v-model="searchSelect">
            <option value="">-</option>
            <option v-for="item in searchList" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div>
        <div class="search-result">
          {{ length }}
        </div>
      </div>
    </div>
    <div class="main-body">
      <DataTable :table-list="eApplicationStore.tableList" :data-list="eApplicationStore.applications" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="openEditDialog" @length="length = $event" :search-key="searchKey" :search-select="searchSelect" :search-list="searchList" :search-color="searchColor" />
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <EditDialog v-model:visible="showPopupEdit" :show-delete-button="isEdit" :show-copy-button="isEdit" large :save-button-text="isEdit ? '保存' : '登録'" @save="onSave" @copy="onCopy" @delete="onDelete">
    <template v-slot:title>{{ isEdit ? '編集' : '新規登録' }}</template>
    <template v-slot:select>
      <div class="select">
        <select v-model="eApplicationStore.formData.status">
          <option></option>
          <option v-for="item in eApplicationStore.statusOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </option>
        </select>
      </div>
    </template>
    <template v-slot:content>
      <div class="dialog-edit">
        <div class="dialog-grid wide">
          <dl class="sticky">
            <dt>会社名</dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.companyId" />
              <div class="select">
                <select v-model="eApplicationStore.formData.companyId" @change="onCompanyChange">
                  <!-- 削除された会社の場合 -->
                  <option v-if="companySelectState === 'deleted'" value="null" disabled>削除された会社</option>
                  <!-- 会社選択を促す場合 -->
                  <option v-else-if="companySelectState === 'select'" value="null" disabled>会社を選択</option>
                  <!-- 通常の無効状態の場合 -->
                  <option v-else value="null" disabled>会社を選択</option>
                  <option v-for="company in companyOptions" :key="company.value" :value="company.value">
                    {{ company.label }}
                  </option>
                </select>
              </div>
            </dd>
            <dt>事業場</dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.branchId" />
              <div class="select">
                <select v-model="eApplicationStore.formData.branchId" @change="onBranchChange" :disabled="!eApplicationStore.formData.companyId">
                  <!-- 削除された事業場の場合 -->
                  <option v-if="branchSelectState === 'deleted'" value="null" disabled>削除された事業場</option>
                  <!-- 事業場選択を促す場合 -->
                  <option v-else-if="branchSelectState === 'select'" value="null" disabled>事業場を選択</option>
                  <!-- 通常の無効状態の場合 -->
                  <option v-else value="null" disabled>事業場を選択</option>
                  <option v-for="branch in branchOptions" :key="branch.value" :value="branch.value">
                    {{ branch.label }}
                  </option>
                </select>
              </div>
            </dd>
            <dt>従業員</dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.staffId" />
              <div class="select">
                <select v-model="eApplicationStore.formData.staffId" @change="onStaffChange" :disabled="!eApplicationStore.formData.branchId">
                  <!-- 削除された従業員の場合 -->
                  <option v-if="staffSelectState === 'deleted'" value="null" disabled>削除された従業員</option>
                  <!-- 従業員選択を促す場合 -->
                  <option v-else-if="staffSelectState === 'select'" value="null" disabled>従業員を選択</option>
                  <!-- 通常の無効状態の場合 -->
                  <option v-else value="null" disabled>従業員を選択</option>
                  <option v-for="staff in staffOptions" :key="staff.value" :value="staff.value">
                    {{ staff.label }}
                  </option>
                </select>
              </div>
            </dd>
            <dt>健保名</dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.healthInsurance" />
              <input type="text" v-model="eApplicationStore.formData.healthInsurance" />
            </dd>
            <dt>該当日</dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.applicableDate" />
              <input type="date" v-model="eApplicationStore.formData.applicableDate" />
            </dd>
            <dt>内容</dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.content" />
              <input type="text" v-model="eApplicationStore.formData.content" />
            </dd>
            <dt>備考</dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.memo" />
              <input type="text" v-model="eApplicationStore.formData.memo" />
            </dd>
          </dl>
          <div class="border"></div>
          <dl class="aling-left">
            <!-- 雇用保険（作業、受理） -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.employmentInsurance" @change="onCheckChange('employmentInsurance', $event)" />
                雇用保険（作業、受理）
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.employmentInsuranceMain" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.employmentInsuranceMain.date" @change="onDateChange('employmentInsuranceMain', $event)" :disabled="!eApplicationStore.dateCheckStatus.employmentInsurance" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.employmentInsuranceMain?.userName">
                  {{ eApplicationStore.getCheckInfo('employmentInsuranceMain') }}
                </div>
              </div>
              &nbsp;&nbsp;
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.employmentInsuranceSub" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.employmentInsuranceSub.date" @change="onDateChange('employmentInsuranceSub', $event)" :disabled="!eApplicationStore.dateCheckStatus.employmentInsurance" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.employmentInsuranceSub?.userName">
                  {{ eApplicationStore.getCheckInfo('employmentInsuranceSub') }}
                </div>
              </div>
            </dd>

            <!-- 公文書発送日（本人宛） -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.publicDocumentSend" @change="onCheckChange('publicDocumentSend', $event)" />
                公文書発送日（本人宛）
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.publicDocumentSend" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.publicDocumentSend.date" @change="onDateChange('publicDocumentSend', $event)" :disabled="!eApplicationStore.dateCheckStatus.publicDocumentSend" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.publicDocumentSend?.userName">
                  {{ eApplicationStore.getCheckInfo('publicDocumentSend') }}
                </div>
              </div>
            </dd>

            <!-- 健康保険（作業、受理） -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.healthInsurance" @change="onCheckChange('healthInsurance', $event)" />
                健康保険（作業、受理）
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.healthInsuranceDateMain" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.healthInsuranceDateMain.date" @change="onDateChange('healthInsuranceDateMain', $event)" :disabled="!eApplicationStore.dateCheckStatus.healthInsurance" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.healthInsuranceDateMain?.userName">
                  {{ eApplicationStore.getCheckInfo('healthInsuranceDateMain') }}
                </div>
              </div>
              &nbsp;&nbsp;
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.healthInsuranceDateSub" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.healthInsuranceDateSub.date" @change="onDateChange('healthInsuranceDateSub', $event)" :disabled="!eApplicationStore.dateCheckStatus.healthInsurance" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.healthInsuranceDateSub?.userName">
                  {{ eApplicationStore.getCheckInfo('healthInsuranceDateSub') }}
                </div>
              </div>
            </dd>

            <!-- 厚生年金（作業、受理） -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.pension" @change="onCheckChange('pension', $event)" />
                厚生年金（作業、受理）
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.pensionMain" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.pensionMain.date" @change="onDateChange('pensionMain', $event)" :disabled="!eApplicationStore.dateCheckStatus.pension" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.pensionMain?.userName">
                  {{ eApplicationStore.getCheckInfo('pensionMain') }}
                </div>
              </div>
              &nbsp;&nbsp;
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.pensionSub" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.pensionSub.date" @change="onDateChange('pensionSub', $event)" :disabled="!eApplicationStore.dateCheckStatus.pension" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.pensionSub?.userName">
                  {{ eApplicationStore.getCheckInfo('pensionSub') }}
                </div>
              </div>
            </dd>

            <!-- 第三号（作業、受理） -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.dependentSpouse" @change="onCheckChange('dependentSpouse', $event)" />
                第三号（作業、受理）
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.dependentSpouseMain" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.dependentSpouseMain.date" @change="onDateChange('dependentSpouseMain', $event)" :disabled="!eApplicationStore.dateCheckStatus.dependentSpouse" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.dependentSpouseMain?.userName">
                  {{ eApplicationStore.getCheckInfo('dependentSpouseMain') }}
                </div>
              </div>
              &nbsp;&nbsp;
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.dependentSpouseSub" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.dependentSpouseSub.date" @change="onDateChange('dependentSpouseSub', $event)" :disabled="!eApplicationStore.dateCheckStatus.dependentSpouse" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.dependentSpouseSub?.userName">
                  {{ eApplicationStore.getCheckInfo('dependentSpouseSub') }}
                </div>
              </div>
            </dd>

            <!-- 住民税異動届出書 -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.residentTax" @change="onCheckChange('residentTax', $event)" />
                住民税異動届出書
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.residentTax" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.residentTax.date" @change="onDateChange('residentTax', $event)" :disabled="!eApplicationStore.dateCheckStatus.residentTax" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.residentTax?.userName">
                  {{ eApplicationStore.getCheckInfo('residentTax') }}
                </div>
              </div>
            </dd>

            <!-- 源泉徴収票送付確認 -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.withholdingTaxSend" @change="onCheckChange('withholdingTaxSend', $event)" />
                源泉徴収票送付確認
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.withholdingTaxSend" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.withholdingTaxSend.date" @change="onDateChange('withholdingTaxSend', $event)" :disabled="!eApplicationStore.dateCheckStatus.withholdingTaxSend" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.withholdingTaxSend?.userName">
                  {{ eApplicationStore.getCheckInfo('withholdingTaxSend') }}
                </div>
              </div>
            </dd>

            <!-- 資格証明書 -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.qualificationCertificate" @change="onCheckChange('qualificationCertificate', $event)" />
                資格証明書
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.qualificationCertificate" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.qualificationCertificate.date" @change="onDateChange('qualificationCertificate', $event)" :disabled="!eApplicationStore.dateCheckStatus.qualificationCertificate" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.qualificationCertificate?.userName">
                  {{ eApplicationStore.getCheckInfo('qualificationCertificate') }}
                </div>
              </div>
            </dd>

            <!-- 公文書通知（会社宛） -->
            <dt>
              <label>
                <input type="checkbox" v-model="eApplicationStore.dateCheckStatus.publicNotification" @change="onCheckChange('publicNotification', $event)" />
                公文書通知（会社宛）
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="eApplicationStore.formData.colorInfo.publicNotification" />
              <div>
                <input type="date" v-model="eApplicationStore.formData.dateJson.publicNotification.date" @change="onDateChange('publicNotification', $event)" :disabled="!eApplicationStore.dateCheckStatus.publicNotification" />
                <div class="date-user" v-if="eApplicationStore.formData.dateJson.publicNotification?.userName">
                  {{ eApplicationStore.getCheckInfo('publicNotification') }}
                </div>
              </div>
            </dd>
          </dl>
        </div>
      </div>
    </template>
  </EditDialog>
</template>

<style lang="less" scoped>
  .date-user {
    color: #6c757d;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px;
    text-align: right;
    position: absolute;
    margin-top: -5px;
  }
</style>
